import { FC, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import {
  ActivityOverviewIcon,
  CalendarIcon,
  ChevronDownIcon,
  ExportIcon,
  PlusIcon,
} from '../../assets/icons/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import { EmptySearchList } from '../../components/empty-search-list/index';
import { PaginationController } from '../../components/pagination-controller/index';
import { Search } from '../../components/search/search';
import { Select, SelectButton, SelectOption, SelectOptions } from '../../components/select/index';
import {
  dividendItemsToFetchMap,
  DividendPayout,
  DividendPeriodFilter,
  DividendSortBy,
  DividendSortOrder,
} from '../../hooks/dividend/dividend.types';
import { DividendHooks, usePagination, useSelectedCompany } from '../../hooks/index';
import { useShareClasses } from '../../hooks/share-class/use-share-classes';
import { useScreenSize } from '../../hooks/use-screen-size.hook';
import { StringKey } from '../../lang/index';
import { getTranslation } from '../../utils/get-translation.util';
import { AddDividendModal } from './add-dividend/index';
import {
  DividendDetailsSheet,
  DividendDistributionChart,
  DividendsByStakeholderTable,
  DividendsEmptyState,
  DividendsStatsStrip,
  DividendsTable,
} from './components/index';

const ALL_STATUSES_VALUE = '';

const PERIOD_LABELS: Record<DividendPeriodFilter, StringKey> = {
  [DividendPeriodFilter.ALL_TIME]: 'ALL_TIME',
  [DividendPeriodFilter.LAST_6_MONTHS]: 'LAST_6_MONTHS',
  [DividendPeriodFilter.LAST_12_MONTHS]: 'LAST_12_MONTHS',
};

const PERIOD_OPTIONS = Object.values(DividendPeriodFilter);

const ALL_SHARE_CLASSES_VALUE = '';

enum DividendsView {
  BY_PAYOUT = 'by-payout',
  BY_STAKEHOLDER = 'by-stakeholder',
}

const viewTabs: { label: StringKey; value: DividendsView }[] = [
  { label: 'BY_PAYOUT', value: DividendsView.BY_PAYOUT },
  { label: 'BY_STAKEHOLDER', value: DividendsView.BY_STAKEHOLDER },
];

const statusFilterOptions: { label: StringKey; value: '' | DividendHooks.DividendPayoutStatus }[] =
  [
    { label: 'ALL_STATUSES', value: ALL_STATUSES_VALUE },
    { label: 'DIVIDEND_STATUS_PENDING', value: DividendHooks.DividendPayoutStatus.PENDING },
    { label: 'DIVIDEND_STATUS_APPROVED', value: DividendHooks.DividendPayoutStatus.APPROVED },
    { label: 'DIVIDEND_STATUS_PAID_OUT', value: DividendHooks.DividendPayoutStatus.PAID_OUT },
  ];

const Dividends: FC = () => {
  const { selectedCompany } = useSelectedCompany();
  const companyId = selectedCompany?.id ?? '';
  const currencySymbol = selectedCompany?.currency?.symbol ?? '';

  const [currentPage, setCurrentPage, resetPage] = usePagination('dividends');
  const [itemsToFetch, setItemsToFetch] = useState(dividendItemsToFetchMap[0]);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | DividendHooks.DividendPayoutStatus>('');
  const [periodFilter, setPeriodFilter] = useState<DividendPeriodFilter>(
    DividendPeriodFilter.ALL_TIME,
  );
  const [shareClassFilter, setShareClassFilter] = useState<string>(ALL_SHARE_CLASSES_VALUE);
  const [activeView, setActiveView] = useState<DividendsView>(DividendsView.BY_PAYOUT);
  const [sortField, setSortField] = useState<DividendSortBy | null>(null);
  const [sortOrder, setSortOrder] = useState<DividendSortOrder | null>(null);

  const { shareClasses } = useShareClasses({ companyId, filter: 'all', take: 200 });
  const { exportAll, isExportingAll } = DividendHooks.useDividendExport();

  const handleSearchChange = useCallback(
    (value: string) => {
      setDebouncedSearch(value);
      resetPage();
    },
    [resetPage],
  );

  const handleSortClick = (field: DividendSortBy) => {
    if (sortField === field) {
      setSortOrder((prev) =>
        prev === DividendSortOrder.ASC ? DividendSortOrder.DESC : DividendSortOrder.ASC,
      );
    } else {
      setSortField(field);
      setSortOrder(DividendSortOrder.DESC);
    }
    resetPage();
  };

  const handleStatusChange = (value: '' | DividendHooks.DividendPayoutStatus) => {
    setStatusFilter(value);
    resetPage();
  };

  const handlePeriodChange = (value: DividendPeriodFilter) => {
    setPeriodFilter(value);
    resetPage();
  };

  const handleShareClassChange = (value: string) => {
    setShareClassFilter(value);
    resetPage();
  };

  const { records, totalPages, isFetched, isLoading } = DividendHooks.useDividendPayouts({
    companyId,
    search: debouncedSearch || undefined,
    skip: (currentPage - 1) * itemsToFetch,
    sortBy: sortField ?? undefined,
    sortOrder: sortOrder ?? undefined,
    shareClassId: shareClassFilter || undefined,
    status: statusFilter || undefined,
    take: itemsToFetch,
    ...(periodFilter !== DividendPeriodFilter.ALL_TIME && { period: periodFilter }),
  });

  const isFiltered = Boolean(
    debouncedSearch ||
      statusFilter !== ALL_STATUSES_VALUE ||
      periodFilter !== DividendPeriodFilter.ALL_TIME ||
      shareClassFilter !== ALL_SHARE_CLASSES_VALUE,
  );
  const isEmpty = isFetched && !isLoading && records.length === 0;

  const { width } = useScreenSize();
  const isPhone = width < 460;

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editPayout, setEditPayout] = useState<DividendPayout | null>(null);
  const handleAddDividend = useCallback(() => {
    setEditPayout(null);
    setIsAddModalOpen(true);
  }, []);
  const handleCloseAddModal = useCallback(() => {
    setIsAddModalOpen(false);
    setEditPayout(null);
  }, []);

  const [viewPayout, setViewPayout] = useState<DividendPayout | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPayoutId = searchParams.get('payoutId');

  const { payout: requestedPayout } = DividendHooks.useDividendPayout({
    companyId,
    payoutId: requestedPayoutId ?? '',
    enabled: !!requestedPayoutId,
  });

  useEffect(() => {
    if (requestedPayout) setViewPayout(requestedPayout);
  }, [requestedPayout]);

  useEffect(() => {
    setViewPayout((current) => {
      if (!current) return current;

      return records.find(({ id }) => id === current.id) ?? current;
    });
  }, [records]);

  const handleViewPayout = useCallback((payout: DividendPayout) => setViewPayout(payout), []);

  const handleCloseDetailsSheet = useCallback(() => {
    setViewPayout(null);
    if (!requestedPayoutId) return;

    searchParams.delete('payoutId');
    setSearchParams(searchParams, { replace: true });
  }, [requestedPayoutId, searchParams, setSearchParams]);

  const handleEditPayout = useCallback((payout: DividendPayout) => {
    setEditPayout(payout);
    setIsAddModalOpen(true);
  }, []);

  if (isEmpty && !isFiltered) {
    return (
      <>
        <div className="flex h-full w-full flex-col items-center justify-center overflow-y-auto p-4">
          <DividendsEmptyState onClick={handleAddDividend} />
        </div>
        <AddDividendModal
          companyId={companyId}
          currencySymbol={currencySymbol}
          editPayout={editPayout ?? undefined}
          isOpen={isAddModalOpen}
          onClose={handleCloseAddModal}
          onEditDuplicate={handleEditPayout}
        />
        <DividendDetailsSheet
          companyId={companyId}
          currencySymbol={currencySymbol}
          isOpen={viewPayout !== null}
          onClose={handleCloseDetailsSheet}
          payout={viewPayout}
        />
      </>
    );
  }

  const selectedStatusKey =
    statusFilterOptions.find((o) => o.value === statusFilter)?.label ?? 'ALL_STATUSES';
  const selectedStatusLabel = getTranslation(selectedStatusKey);
  const selectedPeriodLabel = getTranslation(PERIOD_LABELS[periodFilter]);
  const selectedShareClassName = shareClasses.find(
    (shareClass) => shareClass.id === shareClassFilter,
  )?.name;
  const selectedShareClassLabel = selectedShareClassName ?? getTranslation('ALL_SHARE_CLASSES');
  // Mobile shows the payout cards only — no view toggle or filters.
  const isByPayout = activeView === DividendsView.BY_PAYOUT || isPhone;

  return (
    <div className="flex h-full w-full flex-col gap-4 overflow-y-auto p-4">
      <div className="flex flex-col gap-4 rounded-md bg-white p-4 shadow-sm max-xs:gap-0 max-xs:bg-transparent max-xs:p-0 max-xs:shadow-none">
        <div className="flex items-center justify-between max-xs:hidden">
          <div className="flex items-center gap-2">
            <ActivityOverviewIcon />
            <span className="text-base font-semibold text-gray-700">
              <AppFormattedMessage id="OVERVIEW" />
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              className="flex items-center gap-1 border border-gray-200 bg-white px-4 py-2"
              disabled={isExportingAll}
              onClick={() => exportAll()}
              styleType="DEFAULT_ROUNDED"
            >
              <ExportIcon className="size-4" />
              <span className="text-sm font-[450] text-gray-700">
                <AppFormattedMessage id="EXPORT" />
              </span>
            </Button>
            <Button
              className="size-18 flex items-center gap-1 px-4 py-2"
              data-permission-required
              onClick={handleAddDividend}
            >
              <PlusIcon />
              <span className="text-sm font-[450] text-white">
                <AppFormattedMessage id="DIVIDEND" />
              </span>
            </Button>
          </div>
        </div>
        <div className="flex gap-4 max-lg:flex-col">
          <DividendsStatsStrip companyId={companyId} />

          <DividendDistributionChart companyId={companyId} currencySymbol={currencySymbol} />
        </div>
      </div>
      <div className="flex flex-col rounded-md bg-white px-4 pb-4 shadow-sm max-xs:px-0">
        <span className="p-4 text-base font-semibold leading-8 text-gray-700">
          <AppFormattedMessage id="DETAILS" />
        </span>

        <div className="flex flex-col overflow-hidden rounded-md shadow-sm max-xs:shadow-none">
          <div className="flex w-full flex-wrap items-center justify-between gap-3 border-b border-gray-100 p-4 max-xs:hidden">
            <div className="flex h-9 divide-x divide-gray-200 overflow-hidden rounded border border-gray-200">
              {viewTabs.map((tab) => (
                <button
                  className={twMerge(
                    'h-full px-4 text-sm font-[450] text-[#858593] transition-colors',
                    activeView === tab.value && 'bg-brand-25 font-[550] text-[#172335]',
                  )}
                  key={tab.value}
                  onClick={() => setActiveView(tab.value)}
                  type="button"
                >
                  <AppFormattedMessage id={tab.label} />
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Search callback={handleSearchChange} />

              <Select<'' | DividendHooks.DividendPayoutStatus>
                onChange={handleStatusChange}
                value={statusFilter}
              >
                {({ open }: { open: boolean }) => (
                  <div className="relative">
                    <SelectButton className="flex h-9 items-center gap-2 rounded border border-gray-200 bg-white px-3">
                      <span className="text-sm font-[450] text-gray-700">
                        {selectedStatusLabel}
                      </span>
                      <ChevronDownIcon
                        className={twMerge(
                          'h-3 w-3 transition-transform duration-200',
                          open && 'rotate-180',
                        )}
                      />
                    </SelectButton>
                    <SelectOptions
                      anchor={{ gap: 4, to: 'bottom end' }}
                      className="z-20 min-w-[160px] rounded border border-gray-200 bg-white shadow-lg"
                    >
                      {statusFilterOptions.map((option) => (
                        <SelectOption key={option.label} value={option.value}>
                          <span className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                            <AppFormattedMessage id={option.label} />
                          </span>
                        </SelectOption>
                      ))}
                    </SelectOptions>
                  </div>
                )}
              </Select>

              <Select<string> onChange={handleShareClassChange} value={shareClassFilter}>
                {({ open }: { open: boolean }) => (
                  <div className="relative">
                    <SelectButton className="flex h-9 items-center gap-2 rounded border border-gray-200 bg-white px-3">
                      <span className="text-sm font-[450] text-gray-700">
                        {selectedShareClassLabel}
                      </span>
                      <ChevronDownIcon
                        className={twMerge(
                          'h-3 w-3 transition-transform duration-200',
                          open && 'rotate-180',
                        )}
                      />
                    </SelectButton>
                    <SelectOptions
                      anchor={{ gap: 4, to: 'bottom end' }}
                      className="z-20 max-h-64 min-w-[180px] overflow-y-auto rounded border border-gray-200 bg-white shadow-lg"
                    >
                      <SelectOption key="all" value={ALL_SHARE_CLASSES_VALUE}>
                        <span className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <AppFormattedMessage id="ALL_SHARE_CLASSES" />
                        </span>
                      </SelectOption>
                      {shareClasses.map((shareClass) => (
                        <SelectOption key={shareClass.id} value={shareClass.id}>
                          <span className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                            {shareClass.name}
                          </span>
                        </SelectOption>
                      ))}
                    </SelectOptions>
                  </div>
                )}
              </Select>

              <Select<DividendPeriodFilter> onChange={handlePeriodChange} value={periodFilter}>
                {({ open }: { open: boolean }) => (
                  <div className="relative">
                    <SelectButton className="flex h-9 items-center gap-2 rounded border border-gray-200 bg-white px-3">
                      <CalendarIcon className="h-4 w-4" />
                      <span className="text-sm font-[450] text-gray-700">
                        {selectedPeriodLabel}
                      </span>
                      <ChevronDownIcon
                        className={twMerge(
                          'h-3 w-3 transition-transform duration-200',
                          open && 'rotate-180',
                        )}
                      />
                    </SelectButton>
                    <SelectOptions
                      anchor={{ gap: 4, to: 'bottom end' }}
                      className="z-20 min-w-[160px] rounded border border-gray-200 bg-white shadow-lg"
                    >
                      {PERIOD_OPTIONS.map((option) => (
                        <SelectOption key={option} value={option}>
                          <span className="block text-nowrap px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                            <AppFormattedMessage id={PERIOD_LABELS[option]} />
                          </span>
                        </SelectOption>
                      ))}
                    </SelectOptions>
                  </div>
                )}
              </Select>
            </div>
          </div>

          {isByPayout ? (
            <>
              {isEmpty && isFiltered ? (
                <EmptySearchList className="m-auto my-20" />
              ) : (
                <div className="w-full overflow-x-auto">
                  <DividendsTable
                    companyId={companyId}
                    currencySymbol={currencySymbol}
                    onEdit={handleEditPayout}
                    onSortClick={handleSortClick}
                    onView={handleViewPayout}
                    payouts={records}
                    sortField={sortField}
                    sortOrder={sortOrder}
                  />
                </div>
              )}

              <div className="px-4 pt-4">
                <PaginationController
                  currentPage={currentPage}
                  itemsCount={records.length}
                  itemsToFetch={itemsToFetch}
                  itemsToFetchMap={dividendItemsToFetchMap}
                  onClick={setCurrentPage}
                  onFetchAmountChange={setItemsToFetch}
                  totalPages={totalPages}
                />
              </div>
            </>
          ) : (
            <DividendsByStakeholderTable
              companyId={companyId}
              currencySymbol={currencySymbol}
              search={debouncedSearch}
              shareClassId={shareClassFilter || undefined}
              status={statusFilter}
              {...(periodFilter !== DividendPeriodFilter.ALL_TIME && { period: periodFilter })}
            />
          )}
        </div>
      </div>

      <AddDividendModal
        companyId={companyId}
        currencySymbol={currencySymbol}
        editPayout={editPayout ?? undefined}
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        onEditDuplicate={handleEditPayout}
      />

      <DividendDetailsSheet
        companyId={companyId}
        currencySymbol={currencySymbol}
        isOpen={viewPayout !== null}
        onClose={handleCloseDetailsSheet}
        payout={viewPayout}
      />
    </div>
  );
};

export default Dividends;
